import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { DefaultTextCellRenderer } from 'Components/organisms/AgGridBase';
import { DepartmentColumnFilter } from 'Components/organisms/AgToolingComplianceGrid/CustomFilters/DepartmentColumnFilter';
import type { PhishingRecipientRow } from './phishingCampaignsTable.types';

const textCellClass = 'text-text-high-emphasis';

export type GetPhishingRecipientColumnsParams = {
  label: (key: string, fallback: string) => string;
  getStatusLabel: (status: string) => string;
  statusFilterValues: readonly string[];
  onViewEmailBody: (body: string, row: PhishingRecipientRow) => void;
};

type SubjectCellRendererParams = ICellRendererParams<PhishingRecipientRow> & {
  onViewEmailBody: (body: string, row: PhishingRecipientRow) => void;
};

function SubjectCellRenderer({ value, data, onViewEmailBody }: SubjectCellRendererParams) {
  // Show the subject text; clicking opens the email body preview modal.
  if (!value || !data) {
    return <span className="text-text-medium-emphasis">-</span>;
  }
  return (
    <button
      type="button"
      className="phishing-campaigns-page__email-body-cell"
      onClick={() => onViewEmailBody(data.emailBody, data)}
      title="Click to preview email"
    >
      {value}
    </button>
  );
}

export function getPhishingRecipientColumns({
  label,
  getStatusLabel,
  statusFilterValues,
  onViewEmailBody,
}: GetPhishingRecipientColumnsParams): ColDef<PhishingRecipientRow>[] {
  return [
    {
      colId: 'name',
      headerName: label('NAME', 'Name'),
      sortable: true,
      filter: false,
      flex: 4,
      minWidth: 130,
      valueGetter: (p) => {
        const d = p.data;
        if (!d) {
          return '';
        }
        const n = d.name?.trim();
        return n || d.email || '';
      },
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'email',
      field: 'email',
      headerName: label('EMAIL_LABEL', 'Email'),
      flex: 4,
      sortable: true,
      filter: false,
      minWidth: 180,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'subject',
      field: 'subject',
      headerName: label('SUBJECT_LABEL', 'Subject'),
      flex: 4,
      sortable: false,
      filter: false,
      minWidth: 160,
      cellRenderer: SubjectCellRenderer,
      cellRendererParams: { onViewEmailBody },
    },
    {
      colId: 'department',
      field: 'department',
      headerName: label('DEPARTMENT_LABEL', 'Department'),
      flex: 3,
      minWidth: 130,
      sortable: true,
      filter: DepartmentColumnFilter,
      filterParams: { buttons: ['reset'], debounceMs: 0 },
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'organization',
      field: 'organization',
      headerName: label('ORGANIZATION_LABEL', 'Organization'),
      flex: 3,
      sortable: true,
      filter: false,
      minWidth: 160,
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'status',
      headerName: label('STATUS', 'Status'),
      sortable: true,
      flex: 2,
      minWidth: 90,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: [...statusFilterValues],
      },
      maxWidth: 140,
      valueGetter: (p) => getStatusLabel(p.data?.status ?? ''),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass, emptyLabel: '-' },
    },
    {
      colId: 'sentAt',
      headerName: label('SENT_AT_LABEL', 'Sent at'),
      sortable: true,
      filter: false,
      flex: 3,
      minWidth: 130,
      valueGetter: (p) => p.data?.sentAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.sentAtMs ?? 0) - (nb.data?.sentAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'openedAt',
      headerName: label('OPENED_AT_LABEL', 'Opened at'),
      sortable: true,
      filter: false,
      flex: 3,
      minWidth: 160,
      valueGetter: (p) => p.data?.openedAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.openedAtMs ?? 0) - (nb.data?.openedAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'clickedAt',
      headerName: label('CLICKED_AT_LABEL', 'Clicked at'),
      sortable: true,
      filter: false,
      flex: 3,
      minWidth: 160,
      valueGetter: (p) => p.data?.clickedAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.clickedAtMs ?? 0) - (nb.data?.clickedAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
    {
      colId: 'reportedAt',
      headerName: label('REPORTED_AT_LABEL', 'Reported at'),
      sortable: true,
      filter: false,
      minWidth: 160,
      flex: 3,
      valueGetter: (p) => p.data?.reportedAt ?? '-',
      comparator: (_a, _b, na, nb) => (na.data?.reportedAtMs ?? 0) - (nb.data?.reportedAtMs ?? 0),
      cellRenderer: DefaultTextCellRenderer,
      cellRendererParams: { className: textCellClass },
    },
  ];
}
